import { useEffect, useState } from 'react';
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import type { Member } from './members';

export async function callSetBoardClaim(
  email: string,
): Promise<{ uid: string }> {
  const fn = httpsCallable<{ email: string }, { uid: string }>(
    functions,
    'setBoardClaim',
  );
  const res = await fn({ email });
  return res.data;
}

export async function callRemoveBoardClaim(
  email: string,
): Promise<{ uid: string }> {
  const fn = httpsCallable<{ email: string }, { uid: string }>(
    functions,
    'removeBoardClaim',
  );
  const res = await fn({ email });
  return res.data;
}

export interface CreateMemberInput {
  email: string;
  firstName: string;
  lastName: string;
  address: string;
  postalCode: string;
  city: string;
  alternateAddress: string | null;
  phoneLandline: string | null;
  phoneMobile: string | null;
  spouse:
    | { name: string; email: string | null; phone: string | null }
    | null;
  boatPlaceIds: string[];
}

export async function callCreateMember(
  input: CreateMemberInput,
): Promise<{ uid: string; email: string }> {
  const fn = httpsCallable<CreateMemberInput, { uid: string; email: string }>(
    functions,
    'createMember',
  );
  const res = await fn(input);
  return res.data;
}

export interface DeleteSummary {
  placesDetached: number;
  boatsAffected: number;
  boatsDeleted: boolean;
}

export async function callDeleteMember(
  uid: string,
  deleteBoats: boolean,
): Promise<DeleteSummary> {
  const fn = httpsCallable<
    { uid: string; deleteBoats: boolean },
    DeleteSummary
  >(functions, 'deleteMember');
  const res = await fn({ uid, deleteBoats });
  return res.data;
}

export interface UseAdminsResult {
  admins: (Member & { id: string })[];
  loading: boolean;
  error: Error | null;
}

export function useAdmins(): UseAdminsResult {
  const [admins, setAdmins] = useState<(Member & { id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'members'), where('role', '==', 'board'));
    return onSnapshot(
      q,
      (snap) => {
        setAdmins(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Member) })),
        );
        setLoading(false);
        setError(null);
      },
      (e) => {
        setError(e);
        setLoading(false);
      },
    );
  }, []);

  return { admins, loading, error };
}
